import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import MetricCard from "@sg/pages/BusinessManagement/MetricCard";
import { PUBLISHING_MKTCOST_TOTAL_MKTCOST_CARD, PUBLISHING_MKTCOST_TOTAL_REVENUE_CARD, COEFFICIENT } from '../config';
import CardCommon from '@sg/components/CardCommon';
import TableContent from '@sg/components/TableContent';
import { getTableCapsuleConfig } from '@sg/utils/business';
import SpecialTableBar from '@sg/components/CapsuleBar/SpecialTableBar';
import { Table, Button, Select, notification, Modal } from "tea-component";
import PlusIcon from '@sg/assests/svg/plus-icon.svg';
import { useIntl } from 'umi';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import moment from 'moment';
import './index.scss'
import PlainText from '@sg/components/PlainText';
import TimeLine from '@sg/components/TimeLine';
import { getMktCostCalendarProjects, getMktCostCalendarList, updateMktCostCalendar, deleteMktCostCalendarCard, getMktCostByProject, updateMktCostProjects, getMktCostExecutiveSummary, getMktCostProjectList } from '@sg/services/index';

import { messageTips } from '@gp/utils/common';
import { getProjectColumns, editProjectColums } from './pageConfig';
import { scrollable } from 'tea-component/lib/table/addons';
import { getTimelineDateRange } from '@sg/components/TimeLine/utils';
import { getAppendixNotesPermission, getButtonPermission } from "@sg/_config/permission";
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { createBtnPoint, SGBuriedBtnPoint, MKTCostTab } from '@/pages/strategic/utils/buriedPoint';
import { EDIT_PAGE_KEY_MAP } from '@sg/hooks/useAutoSaveParams';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import { useMKTCostAutoSave } from './hooks/useMKTCostAutoSave';
import { useMKTCostCalendarAutoSave } from './hooks/useMKTCostCalendarAutoSave';
import { isCategoryPredicate, expandCategoriesAfterDrag } from '@sg/utils/tableExpandUtils';
import { useExpandCollapse } from '@/pages/strategic/hooks/useExpandCollapse';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { buildUpdateTime, buildPeriod, sortByAlphaNumericText } from '@sg/utils/common';
interface RecordPhase {
  id: string | number;
  children?: RecordPhase[];
}

export default function MKTCost(props: any) {
  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const { selectedMonth, onEditingChange, maxDataTime } = props;

  const selectedMonthMoment = useMemo(() => {
    return selectedMonth ? moment(selectedMonth, 'YYYY-MM') : null;
  }, [selectedMonth]);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  // 记录待删除的卡片列表
  const [pendingDeletes, setPendingDeletes] = useState<any[]>([]);

  // Project options for dropdown (calendar - TimeLine component)
  const [calendarProjectOptions, setCalendarProjectOptions] = useState < any[] > ([]);
  // Project options for dropdown (table - Add Project)
  const [projectOptions, setProjectOptions] = useState < any[] > ([]);
  // Raw project list from API (without disabled states) - stored to avoid re-fetching
  const rawProjectListRef = useRef<any[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);

  // Ref for TimeLine component to access validation
  const timelineRef = useRef < any > (null);

  const { expandable, selectable, indentable, injectable, mergeable, draggable } = Table.addons;

  const [expandedKeys, setExpandedKeys] = useState(['2']);
  const expandedKeysRef = useRef<string[]>(['2']);
  const isTrackingRef = useRef<boolean>(false);

  // Sync ref with state when state changes from other sources
  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys]);
  const [recordsPhase, setRecordsPhase] = useState < RecordPhase[] > ([]);
  const [mktCostProjectsData, setMktCostProjectsData] = useState < any[] > ([]);
  const [mktCostUpdateTime, setMktCostUpdateTime] = useState<string>('');
  const [calendarUpdateTime, setCalendarUpdateTime] = useState < string > ('');
  // State for executive summary data (capsule charts)
  const [executiveSummaryData, setExecutiveSummaryData] = useState < any > (null);

  // Dynamic MetricCard configs
  const mktCostCardConfig = useMemo(() => {
    return PUBLISHING_MKTCOST_TOTAL_MKTCOST_CARD(executiveSummaryData);
  }, [executiveSummaryData]);

  const revenueCardConfig = useMemo(() => {
    return PUBLISHING_MKTCOST_TOTAL_REVENUE_CARD(executiveSummaryData);
  }, [executiveSummaryData]);
  const [originalMktCostData, setOriginalMktCostData] = useState < any[] > ([]); // 保存原始数据用于对比

  const buttonPermission = useMemo(() => ({
    mktCostByProject: getButtonPermission('publishing_mktcost_mktCostByProject'),
    mktBudgetApplicationCalendar: getButtonPermission('publishing_mktcost_mktBudgetApplicationCalendar'),
    notes: getButtonPermission('publishing_mktcost_notes'),
  }), [permissionReady]);

  // MKT Cost 表格编辑状态
  const MKTRef = useRef < any > (null);
  const [isMKTEditing, setIsMKTEditing] = useState(false);
  // PlainText (Notes) 编辑状态
  const [isNotesEditing, setIsNotesEditing] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0); // 选中的项目数量
  const [moveToValue, setMoveToValue] = useState < string | undefined > (undefined); // 下拉框的值
  const isUpdatingRef = useRef(false); // 标志位，防止onEditDataChange循环调用
  const previousEditDataRef = useRef<any[]>([]); // Track previous edit data to detect deletions

  // 展开/折叠所有有 children 的目录 (must be defined before useMKTCostAutoSave hook)
  const { isAllExpanded, toggleExpandAll, expandAllCategoriesWithChildren } = useExpandCollapse({
    expandedKeys,
    setExpandedKeys,
    expandedKeysRef,
    data: mktCostProjectsData,
    predicate: isCategoryPredicate,
  });

  // 🚀 Auto-save for MKT Cost by Projects - Using custom hook
  const {
    MKTAutoSaveVisible,
    hasMKTUnsavedChanges,
    setMKTPendingDeletes,
    handleMKTEdit,
    handleMKTAutoSaveCancel,
    handleMKTAutoSaveConfirm,
    clearMKTAutoSave,
    setManualUpdate,
  } = useMKTCostAutoSave(isMKTEditing, mktCostProjectsData, MKTRef, setMktCostProjectsData, expandAllCategoriesWithChildren);

  // Helper function to find project name by id in mktCostProjectsData
  const findProjectNameById = useCallback((id: string): string => {
    const findInData = (data: any[]): string | null => {
      for (const record of data) {
        if (String(record.id) === String(id)) {
          // Return Project name
          return record.Project || 'Unknown';
        }
        if (record.children && Array.isArray(record.children)) {
          const found: string | null = findInData(record.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInData(mktCostProjectsData) || 'Unknown';
  }, [mktCostProjectsData]);

  // Internal function to handle expanded keys change with tracking control
  const handleExpandedKeysChangeInternal = useCallback((keys: string[], shouldTrack: boolean = true) => {
    // Prevent double tracking
    if (isTrackingRef.current) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }
    
    // Use ref to get the previous keys to avoid stale closure issues
    const previousKeys = expandedKeysRef.current;
    
    // Only track if keys actually changed
    const previousKeysSorted = [...previousKeys].sort().join(',');
    const keysSorted = [...keys].sort().join(',');
    const keysChanged = previousKeysSorted !== keysSorted;
    
    if (!keysChanged) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }
    
    // Set tracking flag to prevent double tracking
    isTrackingRef.current = true;
    
    // Only track buried points if shouldTrack is true (user manually clicked project)
    if (shouldTrack) {
      // Find which project was expanded or collapsed
      const newlyExpanded = keys.filter(key => !previousKeys.includes(key));
      const newlyCollapsed = previousKeys.filter(key => !keys.includes(key));
      
      // Track expansion
      if (newlyExpanded.length > 0) {
        newlyExpanded.forEach(key => {
          const projectName = findProjectNameById(key);
          const btnPoint = createBtnPoint(
            MKTCostTab,
            'mktCostByProjectsCollapse',
            `MKT Cost by Projects Expand - ${projectName}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        });
      }
      
      // Track collapse
      if (newlyCollapsed.length > 0) {
        newlyCollapsed.forEach(key => {
          const projectName = findProjectNameById(key);
          const btnPoint = createBtnPoint(
            MKTCostTab,
            'mktCostByProjectsCollapse',
            `MKT Cost by Projects Collapse - ${projectName}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        });
      }
    }
    
    // Update the state and ref, then reset tracking flag
    setExpandedKeys(keys);
    expandedKeysRef.current = keys;
    
    // Reset tracking flag after a short delay to allow state update
    setTimeout(() => {
      isTrackingRef.current = false;
    }, 0);
  }, [findProjectNameById]);

  // Wrapper for expandable addon - always tracks (user clicked project)
  const handleExpandedKeysChange = useCallback((keys: string[], context?: any) => {
    handleExpandedKeysChangeInternal(keys, true);
  }, [handleExpandedKeysChangeInternal]);

  const [maxVsVallue, setMaxVsVallue] = useState(0);
  const calculateMaxVsVallue = (data: any[]) => {
    let max = 0;
    const compareDiff = (entity: any) => {
      // console.log('entity', entity);
      const valueA = entity?.['25A'] || 0;
      const valueB = entity?.['25B'] || 0;
      // If both are 0, or B is 0, show 0%
      const dValue = valueB === 0 ? 0 : (valueA / valueB * 100);
      if (dValue > max) {
        max = dValue;
      }
    };
    data.forEach((item: any) => {
      compareDiff(item);
      if (Array.isArray(item?.children)) {
        item.children.forEach((child: any) => compareDiff(child));
      }
    });
    setMaxVsVallue(max);
    return data;
  };

  // PlainText 组件 ref
  const MKTCostRef = useRef < any > (null);

  // Fetch MKT Cost By Project data
  const fetchMktCostProjects = async () => {
    try {
      setTableLoading(true);
      const payload: any = {};
      
      // Add datatime if selectedMonth is available
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }
      
      const res: any = await getMktCostByProject(payload);
      
      // Transform new API structure to match old table structure
      const categories = res?.data?.categories || [];
      const standaloneProjects: any[] = [];
      const regularCategories: any[] = [];
      
      categories.forEach((category: any) => {
        // Check if this is the special category for standalone projects
        if (category.category_name === '%%%%%%') {
          // Extract projects from this category as standalone projects
          category.projects.forEach((project: any) => {
            standaloneProjects.push({
              id: project.project_id,
              Project: project.project_name,
              '25A': project['25A'],
              '25F': project['25F'],
              '25B': project['25B'],
              'Sustainability': project.sustainability || '',
              isCategory: false, // Standalone project (no parent category)
            });
          });
        } else {
          // Regular category (prefix ID with 'category_' to avoid collisions with project IDs)
          regularCategories.push({
            id: `category_${category.category_name}`,
            Project: category.category_name,
            '25A': category.total_row['25A'],
            '25F': category.total_row['25F'],
            '25B': category.total_row['25B'],
            'Sustainability': category.total_row.sustainability || '',
            isCategory: true, // 明确标识为一级目录
            children: category.projects.map((project: any) => ({
              id: project.project_id,
              Project: project.project_name,
              '25A': project['25A'],
              '25F': project['25F'],
              '25B': project['25B'],
              'Sustainability': project.sustainability || '',
              isCategory: false, // 明确标识为二级项目
            }))
          });
        }
      });
      
      // Combine standalone projects first (at top), then regular categories
      const transformedData = [...standaloneProjects, ...regularCategories];
      calculateMaxVsVallue(transformedData);
      setMktCostProjectsData(transformedData);
      // 自动展开第一个类别
      if (transformedData.length >= 1) {
        setExpandedKeys([String(transformedData[0].id)]);
      }
      setOriginalMktCostData(JSON.parse(JSON.stringify(transformedData))); // 深拷贝保存原始数据
      setMktCostUpdateTime(res?.data?.datatime || '');
    } catch (error) {
      console.error('Error fetching MKT cost projects data:', error);
      notification.error({
        description: 'Failed to load MKT cost projects data. Please try again.',
      });
      setMktCostProjectsData([]);
    } finally {
      setTableLoading(false);
    }
  };

  // 编辑状态变化回调
  const handleMKTEditingChange = useCallback((isEditing: boolean) => {
    setIsMKTEditing(isEditing);
    // Notify parent component about overall editing state
    if (onEditingChange) {
      onEditingChange(isEditing || isNotesEditing || isEditing);
    }
    // 进入编辑模式时，展开所有有 children 的目录
    if (isEditing) {
      const currentData = MKTRef.current?.getTableData?.() || [];
      expandAllCategoriesWithChildren(currentData);
      // Initialize previousEditDataRef when entering edit mode
      previousEditDataRef.current = JSON.parse(JSON.stringify(currentData));
    } else {
      // 退出编辑时清空选择状态和更新标志
      setSelectedCount(0);
      setMoveToValue(undefined);
      isUpdatingRef.current = false; // 重置标志位
      setMKTPendingDeletes([]); // Clear pending deletes when exiting edit mode
      previousEditDataRef.current = []; // Reset previous edit data ref
      // Clear auto-save when user cancels/exits edit mode
      clearMKTAutoSave();
    }
  }, [expandAllCategoriesWithChildren, clearMKTAutoSave, onEditingChange, isNotesEditing]);

  // Calendar editing change handler
  const handleCalendarEditingChange = useCallback((editing: boolean) => {
    setIsEditing(editing);
    // Notify parent component about overall editing state
    if (onEditingChange) {
      onEditingChange(editing || isMKTEditing || isNotesEditing);
    }
  }, [onEditingChange, isMKTEditing, isNotesEditing]);

  // Notes editing change handler
  const handleNotesEditingChange = useCallback((editing: boolean) => {
    setIsNotesEditing(editing);
    // Notify parent component about overall editing state
    if (onEditingChange) {
      onEditingChange(editing || isMKTEditing || isEditing);
    }
  }, [onEditingChange, isMKTEditing, isEditing]);

  // 生成一级目录选项（用于批量移动下拉框）
  const [MKTOEditData, setMKTOEditData] = useState < any[] > ([]);
  const MKTOptions = useMemo(() => {
    return MKTOEditData
      .filter((item: any) => item.isCategory) // 只选择一级目录
      .map((item: any) => ({
        text: item.Project, // label 是一级目录名
        value: String(item.id), // value 是一级目录 id
      }));
  }, [MKTOEditData]);

  // 通用函数：重新计算每个一级目录的汇总数据（25A、25F、25B）
  const recalculateCategoryTotals = (data: any[]) => {
    return data.map((category: any) => {
      // 如果有 children 属性（一级目录），重新计算汇总
      if (category.children !== undefined) {
        if (Array.isArray(category.children) && category.children.length > 0) {
          // 有子项目：计算所有二级项目的汇总
          // Only sum non-null values, if all are null, result should be null
          const totals = category.children.reduce(
            (acc: any, child: any) => {
              // For each field, only add if the child value is not null/undefined
              const add25A = child['25A'] !== null && child['25A'] !== undefined ? child['25A'] : 0;
              const add25F = child['25F'] !== null && child['25F'] !== undefined ? child['25F'] : 0;
              const add25B = child['25B'] !== null && child['25B'] !== undefined ? child['25B'] : 0;
              
              return {
                '25A': acc['25A'] === null ? (add25A || null) : acc['25A'] + add25A,
                '25F': acc['25F'] === null ? (add25F || null) : acc['25F'] + add25F,
                '25B': acc['25B'] === null ? (add25B || null) : acc['25B'] + add25B,
                // Track if we've seen any non-null values
                hasAny25A: acc.hasAny25A || (child['25A'] !== null && child['25A'] !== undefined),
                hasAny25F: acc.hasAny25F || (child['25F'] !== null && child['25F'] !== undefined),
                hasAny25B: acc.hasAny25B || (child['25B'] !== null && child['25B'] !== undefined),
              };
            },
            { '25A': null, '25F': null, '25B': null, hasAny25A: false, hasAny25F: false, hasAny25B: false }
          );

          return {
            ...category,
            // If no children had non-null values, keep as null; otherwise use the sum
            '25A': totals.hasAny25A ? (totals['25A'] || 0) : null,
            '25F': totals.hasAny25F ? (totals['25F'] || 0) : null,
            '25B': totals.hasAny25B ? (totals['25B'] || 0) : null,
          };
        } else {
          // 没有子项目：保持原值（不转换 null 为 0）
          return category;
        }
      }
      // 二级项目保持不变
      return category;
    });
  };

  // 添加新项目行（一级目录）
  const handleAddProjectCategory = () => {
    const timestamp = Date.now();
    const newProject = {
      id: `new_project_${timestamp}`,
      Project: '',
      '25A': null,
      '25F': null,
      '25B': null,
      'Sustainability': 'On Track',
      isCategory: true, // 明确标识为一级目录
      children: [], // 添加 children 属性，标识为一级目录（category）
    };

    if (MKTRef.current) {
      // 更新 editData 和 tableData，在所有 standalone projects 之后添加新分类
      MKTRef.current.setEditData((prevData: any[]) => {
        // 找到最后一个 standalone project 的索引
        const lastStandaloneIndex = prevData.reduce((lastIndex, item, index) => {
          if (item.isCategory === false) {
            return index;
          }
          return lastIndex;
        }, -1);
        
        // 如果有 standalone projects，在它们之后插入；否则在开头插入
        if (lastStandaloneIndex >= 0) {
          const newData = [...prevData];
          newData.splice(lastStandaloneIndex + 1, 0, newProject);
          return newData;
        } else {
          return [newProject, ...prevData];
        }
      });
      MKTRef.current.setTableData((prevData: any[]) => {
        // 找到最后一个 standalone project 的索引
        const lastStandaloneIndex = prevData.reduce((lastIndex, item, index) => {
          if (item.isCategory === false) {
            return index;
          }
          return lastIndex;
        }, -1);
        
        // 如果有 standalone projects，在它们之后插入；否则在开头插入
        if (lastStandaloneIndex >= 0) {
          const newData = [...prevData];
          newData.splice(lastStandaloneIndex + 1, 0, newProject);
          return newData;
        } else {
          return [newProject, ...prevData];
        }
      });
    }
  };

  const handleAddProject = () => {
    const timestamp = Date.now();
    const newProject = {
      id: `new_project_${timestamp}`,
      Project: '',
      '25A': null,
      '25F': null,
      '25B': null,
      'Sustainability': 'On Track',
      isCategory: false, // 明确标识为项目（非目录）
    };

    if (MKTRef.current) {
      // 更新 editData 和 tableData，在顶部添加新 standalone project
      MKTRef.current.setEditData((prevData: any[]) => {
        return [newProject, ...prevData];
      });
      MKTRef.current.setTableData((prevData: any[]) => {
        return [newProject, ...prevData];
      });
    }
  };

  // 保存 MKT Cost 数据
  const handleSaveMKTCost = async (editedData: any[]) => {
    try {
      setTableLoading(true);

      const apiData: any = {
        categories_to_delete: [],
        categories_to_create: [],
        categories_to_update: [],
        category_notes: [],
        category_orders: [],
        projects_to_move: [],
        projects_to_create: [],
        projects_to_delete: [],
        project_orders: [],
        project_notes: [],
        datatime: mktCostUpdateTime || maxDataTime,
      };

      // 构建原始数据的 Map（用于快速查找）
      const originalCategoriesMap = new Map();
      const originalProjectsMap = new Map();

      originalMktCostData.forEach((item: any) => {
        // Use isCategory to distinguish between categories and standalone projects
        if (item.isCategory === true) {
          // This is a category
          originalCategoriesMap.set(item.id, item);
          // Add its children projects to originalProjectsMap
          if (item.children && Array.isArray(item.children)) {
            item.children.forEach((project: any) => {
              const projectData = {
                ...project,
                category: item.id, // 记录原始所属的一级目录
              };
              originalProjectsMap.set(project.id, projectData);
            });
          }
        } else {
          // This is a standalone project (isCategory is false or undefined)
          // Set category to '%%%%%%' to represent the special standalone category
          // This allows move detection to work when standalone projects are moved to regular categories
          originalProjectsMap.set(item.id, {
            ...item,
            // Standalone projects don't have a parent category
          });
        }
      });

      // 构建编辑后数据的 Map（用于快速查找）
      const editedCategoriesMap = new Map();
      const editedProjects: any[] = []; // Track projects (not categories)
      const editedProjectIds = new Set<string>(); // Track all project IDs in edited data
      
      editedData.forEach((item: any) => {
        // Use isCategory as the definitive identifier
        // A category without children is still a category
        if (item.isCategory === true) {
          editedCategoriesMap.set(item.id, item);
          // Also collect project IDs from category children
          if (item.children && Array.isArray(item.children)) {
            item.children.forEach((child: any) => {
              if (child.id) {
                editedProjectIds.add(String(child.id));
              }
            });
          }
        } else {
          // Project (isCategory is false or undefined)
          editedProjects.push(item);
          if (item.id) {
            editedProjectIds.add(String(item.id));
          }
        }
      });

      // 检查被删除的 projects (existing projects only, not newly created ones)
      originalProjectsMap.forEach((originalProject: any, projectId: string) => {
        // Check if project is missing from edited data
        if (!editedProjectIds.has(projectId)) {
          // Only add to delete list if it's not a newly created project
          // (newly created projects have IDs starting with 'new_project_')
          if (!String(projectId).startsWith('new_project_')) {
            apiData.projects_to_delete.push(projectId);
          }
        }
      });

      // 检查被删除的 categories (only check actual categories, not standalone projects)
      originalMktCostData.forEach((originalCategory: any) => {
        // Only process items that are actually categories
        if (originalCategory.isCategory === true && !editedCategoriesMap.has(originalCategory.id)) {
          apiData.categories_to_delete.push(originalCategory.Project);
        }
      });

      // 检查新增的 categories
      editedCategoriesMap.forEach((editedCategory: any) => {
        if (!originalCategoriesMap.has(editedCategory.id)) {
          apiData.categories_to_create.push({
            category_name: editedCategory.Project
          });
        }
      });

      // Counter for custom projects to ensure unique IDs (temp-01, temp-02, etc.)
      let customProjectCounter = 0;
      const getCustomProjectId = () => {
        customProjectCounter++;
        return `temp-${String(customProjectCounter).padStart(2, '0')}`;
      };

      // 检查名字改变的 categories 和 Sustainability 变动
      let categoryIndex = 0;
      editedCategoriesMap.forEach((editedCategory: any) => {
        const index = categoryIndex++;
        const originalCategory = originalCategoriesMap.get(editedCategory.id);
        if (originalCategory) {
          // 检查名字是否改变
          if (originalCategory.Project !== editedCategory.Project) {
            apiData.categories_to_update.push({
              old_category_name: originalCategory.Project,
              new_category_name: editedCategory.Project
            });
          }

          // 检查 Sustainability 是否变化
          if (originalCategory['Sustainability'] !== editedCategory['Sustainability']) {
            apiData.category_notes.push({
              category_name: editedCategory.Project, // 使用编辑后的名字
              sustainability: editedCategory['Sustainability'] || ''
            });
          }

          // 记录一级目录顺序
          apiData.category_orders.push({
            category_name: editedCategory.Project, // 使用编辑后的名字
            order: index + 1
          });
        } else {
          // 新增的 category，检查是否有 notes 内容
          if (editedCategory['Sustainability'] && editedCategory['Sustainability'].trim() !== '') {
            apiData.category_notes.push({
              category_name: editedCategory.Project,
              sustainability: editedCategory['Sustainability'] || ''
            });
          }
        }

        // 检查二级项目的变动
        if (editedCategory.children && Array.isArray(editedCategory.children)) {
          editedCategory.children.forEach((editedProject: any, pIndex: number) => {
            const originalProject = originalProjectsMap.get(editedProject.id);
            
            // Debug logging
            // console.log('🔍 [DEBUG] Processing project:', {
            //   projectId: editedProject.id,
            //   projectName: editedProject.Project,
            //   categoryName: editedCategory.Project,
            //   categoryId: editedCategory.id,
            //   hasOriginalProject: !!originalProject,
            //   originalProjectCategory: originalProject?.category,
            // });
            
            // Check if this is a newly created project (id starts with 'new_project_' and not in original map)
            const isNewProject = editedProject.id && String(editedProject.id).startsWith('new_project_') && !originalProject;
            
            if (isNewProject) {
              // Newly created project: add to projects_to_create
              // Check if the project name matches an existing project from the API
              const projectName = editedProject.Project || '';
              const matchingProject = projectOptions.find((opt: any) => opt.text === projectName);
              const projectId = matchingProject ? matchingProject.value : getCustomProjectId(); // Use actual project_id if found, otherwise unique custom ID
              
              apiData.projects_to_create.push({
                project_id: projectId,
                project_name: projectName
              });
              
              // For newly created projects placed under a category, add to projects_to_move
              // No need to check if it was moved since it's a new project
              apiData.projects_to_move.push({
                project_id: projectId, // Use the project_id from projects_to_create
                new_category_name: editedCategory.Project // Use the category name
              });
              
              // Track order for newly created project within its category
              apiData.project_orders.push({
                project_id: projectId, // Use the project_id from projects_to_create
                order: pIndex + 1
              });
              
              // For new projects, only include project_notes if user actually edited values
              const has25A = editedProject['25A'] !== undefined && editedProject['25A'] !== null;
              const has25F = editedProject['25F'] !== undefined && editedProject['25F'] !== null;
              const has25B = editedProject['25B'] !== undefined && editedProject['25B'] !== null;
              const hasSustainability = !!editedProject['Sustainability'];

              if (has25A || has25F || has25B || hasSustainability) {
                const noteData: any = {
                  project_id: projectId,
                };
                if (has25A) noteData['25A'] = editedProject['25A'];
                if (has25F) noteData['25F'] = editedProject['25F'];
                if (has25B) noteData['25B'] = editedProject['25B'];
                if (hasSustainability) noteData.sustainability = editedProject['Sustainability'];

                apiData.project_notes.push(noteData);
              }
            } else if (originalProject) {
              // Existing project: check for changes
              // 检查项目是否移动到其他一级目录
              const categoryId = editedCategory.id;
              const originalCategory = originalProject.category;
              const isMoved = originalCategory !== categoryId;

              // Debug logging for move detection
              // console.log('🔍 [DEBUG] Move detection:', {
              //   projectId: editedProject.id,
              //   projectName: editedProject.Project,
              //   currentCategoryId: categoryId,
              //   currentCategoryName: editedCategory.Project,
              //   originalCategory: originalCategory,
              //   isMoved: isMoved,
              //   comparison: `${originalCategory} !== ${categoryId}`,
              //   types: {
              //     originalType: typeof originalCategory,
              //     currentType: typeof categoryId,
              //   }
              // });

              // 如果移动了，记录到 projects_to_move
              if (isMoved) {
                apiData.projects_to_move.push({
                  project_id: editedProject.id,
                  new_category_name: editedCategory.Project // 使用编辑后的一级目录名
                });
                // 同时记录在新目录中的排序位置
                apiData.project_orders.push({
                  project_id: editedProject.id,
                  order: pIndex + 1
                });
              } else {
                // 如果没有移动，记录排序
                apiData.project_orders.push({
                  project_id: editedProject.id,
                  order: pIndex + 1
                });
              }

              // 检查项目数据是否变化
              const has25AChanged = originalProject['25A'] !== editedProject['25A'];
              const has25FChanged = originalProject['25F'] !== editedProject['25F'];
              const has25BChanged = originalProject['25B'] !== editedProject['25B'];
              const hasSustainabilityChanged = originalProject['Sustainability'] !== editedProject['Sustainability'];

              // 如果数据变化了，记录到 project_notes
              // Only include fields that have changed (according to API: 有编辑才传)
              if (has25AChanged || has25FChanged || has25BChanged || hasSustainabilityChanged) {
                const noteData: any = {
                  project_id: editedProject.id,
                };
                
                // Only include fields that have changed
                if (has25AChanged) {
                  noteData['25A'] = editedProject['25A'];
                }
                if (has25FChanged) {
                  noteData['25F'] = editedProject['25F'];
                }
                if (has25BChanged) {
                  noteData['25B'] = editedProject['25B'];
                }
                if (hasSustainabilityChanged) {
                  noteData.sustainability = editedProject['Sustainability'] || '';
                }
                
                apiData.project_notes.push(noteData);
              }
            }
          });
        }
      });

      // Handle projects (not categories) - standalone projects
      editedProjects.forEach((editedProject: any, standaloneIndex: number) => {
        const originalProject = originalProjectsMap.get(editedProject.id);
        
        // Check if this is a newly created project
        const isNewProject = editedProject.id && String(editedProject.id).startsWith('new_project_') && !originalProject;
        
        if (isNewProject) {
          // Newly created project: add to projects_to_create
          // Check if the project name matches an existing project from the API
          const projectName = editedProject.Project || '';
          const matchingProject = projectOptions.find((opt: any) => opt.text === projectName);
          const projectId = matchingProject ? matchingProject.value : getCustomProjectId(); // Use actual project_id if found, otherwise unique custom ID
          
          apiData.projects_to_create.push({
            project_id: projectId,
            project_name: projectName
          });
          
          // Track order for newly created standalone project
          apiData.project_orders.push({
            project_id: projectId, // Use the project_id from projects_to_create
            order: standaloneIndex + 1
          });
          
          // For new standalone projects, only include project_notes if user actually edited values
          const has25A = editedProject['25A'] !== undefined && editedProject['25A'] !== null;
          const has25F = editedProject['25F'] !== undefined && editedProject['25F'] !== null;
          const has25B = editedProject['25B'] !== undefined && editedProject['25B'] !== null;
          const hasSustainability = !!editedProject['Sustainability'];

          if (has25A || has25F || has25B || hasSustainability) {
            const noteData: any = {
              project_id: projectId,
            };
            if (has25A) noteData['25A'] = editedProject['25A'];
            if (has25F) noteData['25F'] = editedProject['25F'];
            if (has25B) noteData['25B'] = editedProject['25B'];
            if (hasSustainability) noteData.sustainability = editedProject['Sustainability'];

            apiData.project_notes.push(noteData);
          }
        } else if (originalProject) {
          // Existing project: check if it was moved from a category to standalone
          // If originalProject.category exists, it means it was in a category before
          const originalCategory = originalProject.category;
          const isMovedToStandalone = originalCategory !== undefined && originalCategory !== null;
          
          // If moved from category to standalone, add to projects_to_move with null category
          if (isMovedToStandalone) {
            apiData.projects_to_move.push({
              project_id: editedProject.id,
              new_category_name: null // null for standalone projects
            });
          }
          
          // Existing standalone project: track order and check for changes
          // For standalone projects, always track their order (since they're at top level)
          // The order is based on their position in the editedProjects array
          apiData.project_orders.push({
            project_id: editedProject.id,
            order: standaloneIndex + 1
          });
          
          // 检查项目数据是否变化
          const has25AChanged = originalProject['25A'] !== editedProject['25A'];
          const has25FChanged = originalProject['25F'] !== editedProject['25F'];
          const has25BChanged = originalProject['25B'] !== editedProject['25B'];
          const hasSustainabilityChanged = originalProject['Sustainability'] !== editedProject['Sustainability'];

          // 如果数据变化了，记录到 project_notes
          if (has25AChanged || has25FChanged || has25BChanged || hasSustainabilityChanged) {
            const noteData: any = {
              project_id: editedProject.id,
            };
            
            // Only include fields that have changed
            if (has25AChanged) {
              noteData['25A'] = editedProject['25A'];
            }
            if (has25FChanged) {
              noteData['25F'] = editedProject['25F'];
            }
            if (has25BChanged) {
              noteData['25B'] = editedProject['25B'];
            }
            if (hasSustainabilityChanged) {
              noteData.sustainability = editedProject['Sustainability'] || '';
            }
            
            apiData.project_notes.push(noteData);
          }
        }
      });

      // 调用 API
      await updateMktCostProjects(apiData);
      
      // Clear auto-save data on successful save
      clearMKTAutoSave();
      
      // 更新 Executive Summary 数据（MetricCard）
      await fetchExecutiveSummary();
      // 刷新数据
      await fetchMktCostProjects();

      setTableLoading(false);
    } catch (error) {
      console.error('保存失败:', error);
      notification.error({
        description: 'Failed to save MKT cost projects. Please try again.',
      });
      setTableLoading(false);
    }
  };

  // Fetch game list for project dropdown (same as WeeklyProjectHighlight)
  const fetchGameList = async (searchTerm?: string) => {
    try {
      setSearchLoading(true);
      // If search term is provided, use search; otherwise fetch top 10
      const payload = searchTerm
        ? { search: searchTerm }
        : { top_num: 10 };

      const response = await getMktCostCalendarProjects(payload);
      const raw = response?.data || [];

      // Set project options for dropdown (includes cover for icons)
      const games = raw.map((game: any) => {
        // For custom projects (game_id === '0' or 0), use 'custom_${name}' as unique identifier
        const isCustomProject = game.game_id === '0' || game.game_id === 0 || !game.game_id;
        return {
          text: game.name,
          value: isCustomProject ? `custom_${game.name}` : game.game_id,
          actualGameId: game.game_id || '0', // Store the real game_id for saving
          cover: game.cover || '', // Include cover for display
        };
      });
      setCalendarProjectOptions(games);

      // Return the games for immediate use
      return games;
    } catch (err: any) {
      console.error('Error fetching game list:', err);
      notification.error({
        description: 'Failed to load calendar project list. Please try again.',
      });
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search in project dropdown - wrapped in useCallback to prevent infinite loop
  const handleProjectSearch = useCallback((searchTerm: string) => {
    // Only search if there's a search term, otherwise fetch default list
    if (searchTerm && searchTerm.trim()) {
      fetchGameList(searchTerm.trim());
    } else {
      fetchGameList(); // Fetch default top 10
    }
  }, []); // Empty dependency array - fetchGameList doesn't need to be a dependency

  // Update project options disabled state from local data (no API call)
  const updateProjectOptionsDisabledState = useCallback(() => {
    const projects = rawProjectListRef.current;
    if (projects.length === 0) {
      return; // No project list loaded yet, skip
    }
    
    // Extract all project IDs that are already in use
    const usedProjectIds = new Set<string>();
    
    // 1. From saved data (mktCostProjectsData)
    mktCostProjectsData.forEach((category: any) => {
      if (category.children && Array.isArray(category.children)) {
        category.children.forEach((project: any) => {
          // Only mark as used if it's not a newly created project (not starting with 'new_project_')
          if (project.id && !String(project.id).startsWith('new_project_')) {
            usedProjectIds.add(String(project.id));
          }
        });
      }
      // Also check standalone projects (not in categories)
      if (!category.isCategory && category.id && !String(category.id).startsWith('new_project_')) {
        usedProjectIds.add(String(category.id));
      }
    });
    
    // 2. From current edit data (including newly selected projects that haven't been saved yet)
    const currentEditData = MKTRef.current?.getEditData?.() || [];
    currentEditData.forEach((item: any) => {
      // Check projects in categories
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach((project: any) => {
          if (project.id && project.Project) {
            // For newly created projects, check if they have a selected project name
            // that matches a project_id from the API
            if (String(project.id).startsWith('new_project_')) {
              // Find the project_id that matches the project name
              const matchingProject = projects.find(
                (p: any) => p.project_name === project.Project
              );
              if (matchingProject) {
                usedProjectIds.add(String(matchingProject.project_id));
              }
            } else {
              // Existing project
              usedProjectIds.add(String(project.id));
            }
          }
        });
      }
      // Check standalone projects
      if (!item.isCategory && item.id && item.Project) {
        if (String(item.id).startsWith('new_project_')) {
          // Find the project_id that matches the project name
          const matchingProject = projects.find(
            (p: any) => p.project_name === item.Project
          );
          if (matchingProject) {
            usedProjectIds.add(String(matchingProject.project_id));
          }
        } else {
          // Existing project
          usedProjectIds.add(String(item.id));
        }
      }
    });
    
    // Transform to match FollowerRange format: { text, value, text2, disabled }
    const options = projects.map((project: any) => {
      const projectId = String(project.project_id);
      const isUsed = usedProjectIds.has(projectId);
      
      const option = {
        text: project.project_name,
        value: projectId,
        text2: project.publishing_name, // for searching and customize purpose
        disabled: isUsed, // Disable if project is already in use (saved or in edit data)
      };
      
      // Debug log to see text2 values
      // console.log('[Project Option - Test Mode]', {
      //   text: option.text,
      //   text2: option.text2,
      //   full_project: project
      // });
      
      return option;
    });
    
    setProjectOptions(sortByAlphaNumericText(options, (item) => item.text || ''));
  }, [mktCostProjectsData]);

  // Fetch project list from API (only called once on mount or when explicitly needed)
  const fetchProjectList = useCallback(async () => {
    try {
      setProjectOptionsLoading(true);
      const response = await getMktCostProjectList();
      const projects = response?.data?.projects || [];
      
      // Store raw project list in ref to avoid re-fetching
      rawProjectListRef.current = projects;
      
      // Update disabled state from local data
      updateProjectOptionsDisabledState();
      
      return projects;
    } catch (err: any) {
      console.error('Error fetching project list:', err);
      notification.error({
        description: 'Failed to load project list. Please try again.',
      });
      return [];
    } finally {
      setProjectOptionsLoading(false);
    }
  }, [updateProjectOptionsDisabledState]);

  // Fetch data on component mount
  useEffect(() => {
    fetchGameList(); // For calendar (TimeLine component)
    fetchProjectList(); // For table (Add Project dropdown) - only called once on mount
  }, []);

  // Update disabled state when saved data changes (no API call needed)
  useEffect(() => {
    // Only update if we already have project list loaded
    if (rawProjectListRef.current.length > 0) {
      updateProjectOptionsDisabledState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mktCostProjectsData]);

  // 生成子孙关系信息（用于拖拽）
  const relations = useMemo(() => {
    const relationMap: any = {};
    const buildRelations = (records: any[], parentId = "") => {
      for (const record of records) {
        if (parentId) {
          relationMap[String(record.id)] = String(parentId);
        }
        if (record.children && Array.isArray(record.children)) {
          buildRelations(record.children, record.id);
        }
      }
    };
    buildRelations(mktCostProjectsData);
    return relationMap;
  }, [mktCostProjectsData]);

  // Fetch Executive Summary (Capsule Data)
  const fetchExecutiveSummary = async () => {
    try {
      const payload: any = {};
      
      // Add datatime if selectedMonth is available
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }
      
      const res: any = await getMktCostExecutiveSummary(payload);
      setExecutiveSummaryData(res?.data || null);
    } catch (error) {
      console.error('Error fetching executive summary data:', error);
      notification.error({
        description: 'Failed to load executive summary data. Please try again.',
      });
      setExecutiveSummaryData(null);
    }
  };

  // 初始化
  useEffect(() => {
    // 获取Studio Financials数据
    getAllYearData();
    fetchMktCostProjects();
    fetchExecutiveSummary();
  }, []);

  // Refetch data when selected month changes
  useEffect(() => {
    fetchMktCostProjects();
    fetchExecutiveSummary();
  }, [selectedMonth]);

  // Edit mode handlers
  const handleEdit = () => {
    handleCalendarEdit(() => {
      setIsEditing(true);
      handleCalendarEditingChange(true);
    });
  };

  const handleSave = async () => {

    // Trigger validation in TimeLine component
    if (timelineRef.current && timelineRef.current.validateCards) {
      const isValid = timelineRef.current.validateCards();
      if (!isValid) {
        notification.error({
          description: 'Please complete the required fields before confirming',
        });
        return; // Don't save if validation fails
      }
    }

    try {
      // 先处理删除操作
      if (pendingDeletes.length > 0) {
        // 过滤出需要发送删除请求的记录（排除新增的卡片，新增卡片没有 id 或 id 是字符串）
        const cardsToDelete = pendingDeletes.filter((card) => {
          // 如果是新增的卡片（没有 id 或者是字符串类型的 id），不需要删除
          if (!card.id || typeof card.id === 'string') {
            return false;
          }
          return true;
        });

        // 并行执行所有删除操作
        if (cardsToDelete.length > 0) {
          const deletePromises = cardsToDelete.map((card) =>
            deleteMktCostCalendarCard({ id: Number(card.id) })
          );
          await Promise.all(deletePromises);
        }

        // 清空待删除列表
        setPendingDeletes([]);
      }

      // Save the editValues back to cards and get the updated cards synchronously
      let cardsToSave = timelineCards;
      if (timelineRef.current && timelineRef.current.saveCards) {
        // saveCards returns the merged cards immediately AND triggers onCardsChange
        cardsToSave = timelineRef.current.saveCards();
      }

      // Transform the merged cards to API format and save
      const { startDate, endDate } = getTimelineDateRange();
      const payload = cardsToSave.map(card => ({
        date: card.date,
        game_id: card.game_id || '',
        name: card.project || '',
        status: card.status || '',
        remark: card.remark || ''
      }));

      await updateMktCostCalendar({
        start_date: startDate,
        end_date: endDate,
        data: payload,
      });

      messageTips('success', 'Changes saved successfully');
      setIsEditing(false);
      handleCalendarEditingChange(false);

      // Clear auto-save after successful save
      clearCalendarAutoSave();

      // 重新请求数据
      getAllYearData();
      // Refresh the recent games list after save (backend updates based on recent selections)
      fetchGameList('');
      // Refresh executive summary metrics after timeline changes
      fetchExecutiveSummary();
    } catch (error) {
      console.error('Failed to save calendar changes:', error);
      notification.error({
        description: 'Failed to save calendar changes. Please try again.',
      });
    }
  };

  const handleCancel = () => {
    clearCalendarAutoSave();
    setIsEditing(false);
    handleCalendarEditingChange(false);
    // 清空待删除列表
    setPendingDeletes([]);
    // Refetch data to discard any unsaved changes (including new cards)
    getAllYearData();
  };

  // Delete card handler - 不调用接口，只添加到待删除列表
  const handleDeleteCard = async (cardId: number | string): Promise<boolean> => {
    // 找到要删除的卡片
    const cardToDelete = timelineCards.find((card: any) => String(card.id) === String(cardId));
    if (cardToDelete) {
      setPendingDeletes((prev) => [...prev, cardToDelete]);
      // 从卡片列表中移除（视觉上删除）
      setTimelineCards((prevCards) => prevCards.filter((card: any) => String(card.id) !== String(cardId)));
      return true;
    }
    return false;
  };


  //////////////////////////
  // Timeline data structure
  //////////////////////////

  // Use flat array structure for easier drag-and-drop
  const [timelineCards, setTimelineCards] = useState < any[] > ([]);

  // Wrap setTimelineCards with callback
  const handleCardsChange = (newCards: any[]) => {
    setTimelineCards(newCards);
  };

  // 🚀 Auto-save for MKT Budget Application Calendar (TimeLine) - Using custom hook
  const {
    calendarAutoSaveVisible,
    hasCalendarUnsavedChanges,
    handleCalendarEdit,
    handleCalendarAutoSaveCancel,
    handleCalendarAutoSaveConfirm,
    clearCalendarAutoSave,
  } = useMKTCostCalendarAutoSave(
    isEditing,
    timelineCards,
    pendingDeletes,
    setTimelineCards,
    setPendingDeletes,
    timelineRef
  );

  // Timeline data fetching function
  const getAllYearData = async () => {
    try {
      // Use utility function to get date range
      const { startDate, endDate } = getTimelineDateRange();

      const res = await getMktCostCalendarList({
        start_date: startDate,
        end_date: endDate
      });

      // Extract update_time from response and format to YYYY-MM
      if (res?.data?.update_time) {
        const formattedTime = moment(res.data.update_time).format('YYYY-MM-DD'); // Format "2025-11-03 14:40:42" to "2025-11"
        setCalendarUpdateTime(formattedTime);
      }

      // Transform API items to flat card structure (now from data.calendar)
      const flatCards = res?.data?.calendar?.map((item: any) => ({
        id: item.id,
        date: item.date,
        game_id: item.game_id,
        project: item.name,
        cover: item.cover || '', // Cover provided by API
        sort: item.sort,
        status: item.status,
        remark: item.remark,
      })) || [];

      setTimelineCards(flatCards);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      notification.error({
        description: 'Failed to load calendar data. Please try again.',
      });
    }
  };

  const topRef = useRef < HTMLDivElement > (null);
  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 400)
  }, [])

  return (
    <div>
      {/* <div ref={topRef} style={{ position: 'relative', top: '-160px' }}></div> */}
      <div className='dbs-home-studio-card'>
        <MetricCard
          key={mktCostCardConfig.title}
          coefficient={COEFFICIENT}
          config={mktCostCardConfig}
          capsuleMode={'single'}
          capsuleHeight={40}
        />
        <MetricCard
          key={revenueCardConfig.title}
          coefficient={COEFFICIENT}
          config={revenueCardConfig}
          capsuleMode={'single'}
          capsuleHeight={40}
        />
      </div>
      <CardCommon
        title={
          <div style={{ margin: '20px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>{rendTitle({ 
                title: `${formatMessage({ id: 'dbs_mkt_cost_by_projects' })} ($)`, 
                tips: !isMKTEditing
                    ? buildUpdateTime(mktCostUpdateTime)
                    : `${formatMessage({ id: 'dbs_period' })}:  ${buildPeriod(mktCostUpdateTime)}`
              })}</div>
              <div className='dbs-weekly-buttons-wrapper'>
                {buttonPermission.mktCostByProject && (!isMKTEditing ? (
                  <Button
                    onClick={() => {
                      // Track buried point for Edit button
                      const btnPoint = createBtnPoint(
                        MKTCostTab,
                        'mktCostByProjectsEdit',
                        'MKT Cost by Projects - Edit'
                      );
                      if (btnPoint) {
                        SGBuriedBtnPoint(btnPoint);
                      }
                      
                      MKTRef.current?.handleEdit();
                      // Expand all projects when entering edit mode
                      // Use handleExpandedKeysChangeInternal with shouldTrack=false to prevent collapse/expand tracking
                      if (mktCostProjectsData.length > 0) {
                        const allKeys = mktCostProjectsData.map((item: any) => String(item.id));
                        handleExpandedKeysChangeInternal(allKeys, false);
                      }

                      handleMKTEdit();
                    }}
                    style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_edit' })}
                  </Button>
                ) : (
                  <div className="dbs-weekly-buttons-wrapper">
                    <Button
                      onClick={() => {
                        MKTRef.current?.handleSave();
                      }}
                      style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                      className="dbs-gradient-btn"
                    >
                      {formatMessage({ id: 'dbs_confirm' })}
                    </Button>
                    <Button
                      onClick={() => {
                        MKTRef.current?.handleCancel();
                        if (mktCostProjectsData.length > 0) {
                          setExpandedKeys([String(mktCostProjectsData[0]?.id)]);
                        }
                      }}
                      style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                      className="dbs-outline-btn"
                    >
                      {formatMessage({ id: 'dbs_cancel' })}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        }
      >
        <>
          {isMKTEditing && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                type='weak'
                onClick={handleAddProjectCategory}
                className="dbs-add-project-btn"
                style={{ height: '36px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <img src={PlusIcon} alt="Add" className="dbs-add-project-icon" style={{ width: 16 }} />
                {formatMessage({ id: 'dbs_add_project_category' })}
              </Button>
              
              <Button
                type='weak'
                onClick={handleAddProject}
                className="dbs-add-project-btn"
                style={{ height: '36px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <img src={PlusIcon} alt="Add" className="dbs-add-project-icon" style={{ width: 16 }} />
                {formatMessage({ id: 'dbs_add_project' })}
              </Button>
              
              <Select
                options={MKTOptions}
                appearance="button"
                placeholder="Move to"
                value={moveToValue}
                disabled={selectedCount === 0}
                listHeight={Math.min(MKTOptions.length * 36, 5.5 * 36)}
                onChange={(value: string) => {
                  setMoveToValue(value);
                  if (value && MKTRef.current) {
                    MKTRef.current.batchMoveToCategory(value);
                    // 移动完成后清空下拉框
                    setTimeout(() => {
                      setMoveToValue(undefined);
                    }, 0);
                  }
                }}
              />
              <span style={{ color: '#888', fontSize: '14px' }}>
                {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
              </span>
            </div>
          )}
          {isMKTEditing && (() => {
            // Extract year prefix from mktCostUpdateTime for dynamic message
            const yearPrefix = mktCostUpdateTime && mktCostUpdateTime.length >= 4 
              ? mktCostUpdateTime.substring(2, 4) 
              : '25';
            
            return (
              <div style={{ margin: '20px 0', color: '##262832', fontSize: '14px' }}>
                {`${yearPrefix}F and ${yearPrefix}B are sourced from the finance database and not editable. If any updates are required, please reach out to judymao.`}
              </div>
            );
          })()}
          <div style={{ marginTop: '11px' }}>
            <TableContent
              ref={MKTRef}
              className="dbs-revenue-projects-table dbs-mkt-projects-table"
              // height={'360px'}  className='dbs-table-phase'
              recordKey={(record: any) =>
                // 这里要注意，原始数据中数据类型全是字符串，不转成字符串会导致 key 值匹配不上
                String(record.id)
              }
              datas={mktCostProjectsData}
              loading={tableLoading}
              columns={getProjectColumns(maxVsVallue, formatMessage, mktCostUpdateTime, { isAllExpanded, onToggleExpandAll: toggleExpandAll })}
              editColumns={editProjectColums(maxVsVallue, formatMessage, projectOptions, projectOptionsLoading, mktCostUpdateTime)}
              onCancelRefetch={fetchMktCostProjects}
              originalData={originalMktCostData}
              lastResultData={(data: any) => {
                setRecordsPhase(data);
                return data;
              }}
              addons={[
                // 支持表格滚动，高度超过 192 开始显示滚动条
                // scrollable({
                //   maxHeight: '600px',
                //   // minWidth: 2000,
                // }),
                expandable({
                  // 已经展开的产品
                  expandedKeys,
                  // 产品展开为消息行
                  expand: record => record.children || null,
                  // 发生展开行为时，回调更新展开键值 (with buried point tracking)
                  onExpandedKeysChange: handleExpandedKeysChange,
                  // 非编辑态时允许手动展开，编辑态时通过代码控制展开状态
                  shouldRecordExpandable: record => !isMKTEditing && Boolean(record.children),
                  targetColumnKey: "Project",
                  rowExpand: true
                }),
                // indentable({
                //   // 缩进放在「Project」列上
                //   indent: 16,
                //   targetColumnKey: "Project",
                //   // 提供层级关系
                //   relations,
                // }),
                injectable({
                  row: (props, context) => ({
                    style: {
                      ...(props.style || {}),
                      background: context.record?.children ? "#F5F5F8" : undefined,
                    },
                  }),
                }),
                mergeable({
                  headColSpan: columnIndex => {
                    // switch (columnIndex) {
                    //   case 4:
                    //     return 2;
                    //   case 5:
                    //     return 0;
                    // }
                    return 1;
                  },
                }),
              ]}
              // 拖拽addon需要单独处理，通过editingAddons传递
              editingAddons={[
                draggable({
                  expandedKeys,
                  relations,
                  dragType: "tree",
                  childrenColName: "children",
                  onExpandedKeysChange: (keys: any) => {
                    setExpandedKeys(keys);
                  },
                  onDragEnd: (records: any, dragContext: any) => {
                    // 如果正在更新中，跳过此次调用，防止循环
                    if (isUpdatingRef.current) {
                      return;
                    }
                    
                    // 🔥 CRITICAL: Set manual update flag to prevent auto-save polling from overwriting
                    setManualUpdate();
                    
                    // 验证拖拽结果，确保项目不会被嵌套到其他项目中，以及目录不会被嵌套到其他目录中
                    const validateDragResult = (data: any[]): boolean => {
                      // 检查顶层顺序：独立项目必须在目录之前
                      let foundCategory = false;
                      for (let i = 0; i < data.length; i++) {
                        const item = data[i];
                        // 如果找到目录，标记
                        if (item.isCategory === true) {
                          foundCategory = true;
                        }
                        // 如果目录之后又出现独立项目，这是无效的
                        if (foundCategory && item.isCategory === false) {
                          console.warn('Invalid drag: Standalone projects must always be at the top, categories cannot be moved above them', {
                            itemId: item.id,
                            itemName: item.Project,
                            position: i
                          });
                          return false;
                        }
                      }
                      
                      const checkNesting = (items: any[], depth: number = 0, parentIsCategory: boolean = false): boolean => {
                        for (const item of items) {
                          // 如果是项目（非目录），它不应该有 children
                          if (item.isCategory === false && item.children && Array.isArray(item.children) && item.children.length > 0) {
                            console.warn('Invalid drag: Project cannot have children', item);
                            return false;
                          }
                          
                          // 如果是目录，它不应该被嵌套到另一个目录中（目录只能在顶层）
                          if (item.isCategory === true && parentIsCategory) {
                            console.warn('Invalid drag: Category cannot be nested inside another category', item);
                            return false;
                          }
                          
                          // 递归检查子项
                          if (item.children && Array.isArray(item.children)) {
                            if (!checkNesting(item.children, depth + 1, item.isCategory === true)) {
                              return false;
                            }
                          }
                        }
                        return true;
                      };
                      return checkNesting(data);
                    };
                    
                    // 验证拖拽结果
                    if (!validateDragResult(records)) {
                      console.warn('Drag validation failed: Invalid nesting detected.');
                      // 检查是否是顺序问题（目录在独立项目之后）
                      let foundCategory = false;
                      let hasOrderIssue = false;
                      for (let i = 0; i < records.length; i++) {
                        if (records[i].isCategory === true) {
                          foundCategory = true;
                        }
                        if (foundCategory && records[i].isCategory === false) {
                          hasOrderIssue = true;
                          break;
                        }
                      }
                      
                      const errorMessage = hasOrderIssue
                        ? 'Invalid drag operation. Standalone projects must always be at the top. Categories cannot be moved above standalone projects.'
                        : 'Invalid drag operation. Categories cannot be nested inside other categories, and projects cannot be nested inside other projects.';
                      
                      notification.error({
                        description: errorMessage,
                      });
                      
                      // 使用工具函数简化展开逻辑
                      expandCategoriesAfterDrag(mktCostProjectsData, MKTRef, expandAllCategoriesWithChildren, 0);
                      
                      return; // 阻止无效的拖拽
                    }
                    
                    // 获取当前的 editData（包含用户的编辑）
                    const currentEditData = MKTRef.current?.getEditData?.() || [];
                    
                    // 创建一个 Map 用于快速查找编辑数据（递归构建）
                    const editDataMap = new Map();
                    const buildEditMap = (data: any[]) => {
                      data.forEach((item: any) => {
                        editDataMap.set(String(item.id), item);
                        if (item.children && Array.isArray(item.children)) {
                          buildEditMap(item.children);
                        }
                      });
                    };
                    buildEditMap(currentEditData);

                    // 创建一个 Set 来跟踪已处理的项目ID，防止重复
                    const processedProjectIds = new Set<string>();
                    
                    // 递归合并数据，保留所有用户编辑的字段
                    // 同时清理任何无效的嵌套结构（项目不应该有 children）
                    // 并确保每个项目只出现一次（防止拖拽后重复）
                    const mergeEditData = (records: any[], parentIsCategory: boolean = false): any[] => {
                      return records
                        .map((record: any) => {
                          const editedRecord = editDataMap.get(String(record.id));
                          const merged = {
                            ...record,
                            // 保留用户编辑的所有字段（如果有编辑记录）
                            ...(editedRecord ? {
                              Project: editedRecord.Project,
                              '25A': editedRecord['25A'],
                              '25F': editedRecord['25F'],
                              '25B': editedRecord['25B'],
                              'Sustainability': editedRecord['Sustainability'],
                              isCategory: editedRecord.isCategory,
                            } : {}),
                          };

                          // 如果这是项目（非目录），确保没有 children
                          if (merged.isCategory === false) {
                            // 移除任何 children，因为项目不能有子项
                            if (merged.children) {
                              delete merged.children;
                            }
                            
                            // 检查是否已经处理过这个项目（防止重复）
                            const projectId = String(merged.id);
                            if (processedProjectIds.has(projectId)) {
                              // 这个项目已经在其他地方出现了，跳过这个实例（避免重复）
                              return null;
                            }
                            processedProjectIds.add(projectId);
                          } else {
                            // 对于目录，保留 children 字段（递归处理子项）
                            // 只使用 drag result (record) 的结构，不信任 editData 的结构（可能包含旧位置）
                            // 这样可以确保拖拽后的结构正确，同时保留用户编辑的字段值
                            if (record.children && Array.isArray(record.children)) {
                              merged.children = mergeEditData(record.children, true);
                              // 过滤掉 null 值（被标记为重复的项目）
                              merged.children = merged.children.filter((child: any) => child !== null);
                            } else {
                              // 如果 drag result 中没有 children，设置为空数组（不信任 editData 的结构）
                              merged.children = [];
                            }
                          }

                          return merged;
                        })
                        .filter((record: any) => record !== null); // 移除被标记为重复的项目
                    };
                    
                    const mergedRecords = mergeEditData(records);
                    
                    // 清理空目录的 children 数组，确保一致性
                    const cleanupEmptyCategories = (items: any[]): any[] => {
                      return items.map((item: any) => {
                        if (item.isCategory === true) {
                          // 确保目录有 children 数组（即使为空）
                          if (!item.children || !Array.isArray(item.children)) {
                            item.children = [];
                          }
                          // 递归清理子目录
                          item.children = cleanupEmptyCategories(item.children);
                        }
                        return item;
                      });
                    };
                    const cleanedRecords = cleanupEmptyCategories(mergedRecords);
                    
                    // 再次验证合并后的数据
                    if (!validateDragResult(cleanedRecords)) {
                      console.warn('Drag validation failed after merge. Reverting drag.');
                      notification.error({
                        description: 'Invalid drag operation. Please try again.',
                      });
                      
                      // 使用工具函数简化展开逻辑
                      expandCategoriesAfterDrag(mktCostProjectsData, MKTRef, expandAllCategoriesWithChildren, 0);
                      
                      return;
                    }
                    
                    // 设置标志位，表示正在更新
                    isUpdatingRef.current = true;
                    
                    // 重新计算一级目录的汇总数据（因为拖拽可能改变了子项目的位置）
                    const recalculatedData = recalculateCategoryTotals(cleanedRecords);
                    
                    // 更新项目选项的禁用状态（拖拽后可能有新选择）
                    updateProjectOptionsDisabledState();
                    
                    // 更新状态和数据
                    setMktCostProjectsData(recalculatedData);
                    setMKTOEditData(recalculatedData);
                    if (MKTRef.current) {
                      MKTRef.current.setEditData(recalculatedData);
                      MKTRef.current.setTableData(recalculatedData);
                    }
                    
                    // 使用setTimeout确保更新完成后再重置标志位和展开所有目录
                    setTimeout(() => {
                      isUpdatingRef.current = false;
                      // 展开所有有 children 的目录（操作完成后统一处理）
                      expandAllCategoriesWithChildren(recalculatedData);
                    }, 100);
                  },
                })
              ]}
              editable={true}
              userPermissions={{ canEdit: buttonPermission.mktCostByProject }}
              hideButtons={true}
              onEditingChange={handleMKTEditingChange}
              // validateFields={['Project']}
              // validateFilter={(record: any) => {
              //   // 只检查一级分类（有 children 的行）
              //   return record.children && Array.isArray(record.children);
              // }}
              validateBeforeSave={(editData: any[]) => {
                const errors: Array<{ id: string; emptyFields: string[] }> = [];
                
                // Check all records (top-level and nested) for newly created projects with empty Project field
                const checkRecord = (record: any) => {
                  // Check if this is a newly created project
                  if (record.id && String(record.id).startsWith('new_project_')) {
                    if (!record.Project || (typeof record.Project === 'string' && record.Project.trim() === '')) {
                      errors.push({
                        id: String(record.id),
                        emptyFields: ['Project']
                      });
                    }
                  }
                  
                  // Check nested children if any
                  if (record.children && Array.isArray(record.children)) {
                    record.children.forEach(checkRecord);
                  }
                };
                
                editData.forEach(checkRecord);
                
                // Show notification if validation fails
                if (errors.length > 0) {
                  notification.error({
                    description: 'Please complete the required fields before confirming',
                  });
                }
                
                return {
                  isValid: errors.length === 0,
                  errors: errors
                };
              }}
              onSave={handleSaveMKTCost}
              enableSelection={true}
              selectionRelations={relations}
              onSelectionChange={(keys: string[]) => {
                setSelectedCount(keys.length);
              }}
              onEditDataChange={(data: any) => {
                // 如果正在更新中，跳过此次调用，防止循环
                if (isUpdatingRef.current) {
                  return;
                }
                
                // 🗑️ Detect deletions by comparing previous and current edit data
                if (previousEditDataRef.current.length > 0) {
                  // Helper function to extract all IDs from nested data (including children)
                  const extractAllIds = (items: any[]): Set<string> => {
                    const ids = new Set<string>();
                    const traverse = (item: any) => {
                      if (item.id) {
                        ids.add(String(item.id));
                      }
                      if (item.children && Array.isArray(item.children)) {
                        item.children.forEach(traverse);
                      }
                    };
                    items.forEach(traverse);
                    return ids;
                  };
                  
                  const previousIds = extractAllIds(previousEditDataRef.current);
                  const currentIds = extractAllIds(data);
                  
                  // Find deleted IDs (present in previous but not in current)
                  const deletedIds = Array.from(previousIds).filter(id => !currentIds.has(id));
                  
                  if (deletedIds.length > 0) {
                    // Find the deleted items and add to pendingDeletes
                    const deletedItems: any[] = [];
                    const findItemById = (items: any[], targetId: string): any | null => {
                      for (const item of items) {
                        if (String(item.id) === targetId) {
                          return item;
                        }
                        if (item.children && Array.isArray(item.children)) {
                          const found = findItemById(item.children, targetId);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    
                    deletedIds.forEach(id => {
                      const deletedItem = findItemById(previousEditDataRef.current, id);
                      if (deletedItem) {
                        deletedItems.push(deletedItem);
                      }
                    });
                    
                    if (deletedItems.length > 0) {
                      console.log('🗑️ [MKT PENDING DELETES] Adding deleted items:', deletedItems);
                      setMKTPendingDeletes(prev => [...prev, ...deletedItems]);
                    }
                  }
                }
                
                // Update previous edit data ref for next comparison
                previousEditDataRef.current = JSON.parse(JSON.stringify(data));
                
                // Update project options disabled state when edit data changes (no API call)
                // This ensures newly selected projects are immediately disabled
                updateProjectOptionsDisabledState();
                
                // 设置标志位，表示正在更新
                isUpdatingRef.current = true;
                
                // 编辑数据变化时，重新计算汇总并更新下拉选项
                const recalculatedData = recalculateCategoryTotals(data);
                setMKTOEditData(recalculatedData);
                // 更新表格数据，让重新计算的一级目录汇总值显示在表格中
                if (MKTRef.current) {
                  MKTRef.current.setEditData(recalculatedData);
                  MKTRef.current.setTableData(recalculatedData);
                }
                
                // 使用setTimeout确保更新完成后再重置标志位
                setTimeout(() => {
                  isUpdatingRef.current = false;
                }, 0);
              }}
              onBatchMoveToCategory={(data: any, targetCategoryId: string) => {
                // 移动后重新计算每个一级目录的汇总数据
                const recalculatedData = recalculateCategoryTotals(data);
                // 自动展开目标分类，让用户看到移动后的项目
                setExpandedKeys((prevKeys: string[]) => {
                  if (!prevKeys.includes(targetCategoryId)) {
                    return [...prevKeys, targetCategoryId];
                  }
                  return prevKeys;
                });
                return recalculatedData;
              }}
            ></TableContent>
          </div>
        </>
      </CardCommon>

      <CardCommon
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            {rendTitle({ 
              title: formatMessage({ id: 'dbs_mkt_budget_application_calendar' }), 
              tips: calendarUpdateTime ? `${formatMessage({ id: 'dbs_update_on' })} ${calendarUpdateTime}` : '' ,
              tipString: 'Data is updated by the Business Operations Management team.'  
            })}
            {buttonPermission.mktBudgetApplicationCalendar && !isEditing && (
              <Button
                onClick={() => {
                  // Track buried point for MKT Budget Application Calendar Edit button
                  const btnPoint = createBtnPoint(
                    MKTCostTab,
                    'mktBudgetApplicationCalendarEdit',
                    'MKT Budget Application Calendar - Edit'
                  );
                  if (btnPoint) {
                    SGBuriedBtnPoint(btnPoint);
                  }
                  handleEdit();
                }}
                style={{ height: '30px', minWidth: '60px', fontSize: '12px', marginRight: 0 }}
                className="dbs-gradient-btn"
              >
                {formatMessage({ id: 'dbs_edit' })}
              </Button>
            )}
            {buttonPermission.mktBudgetApplicationCalendar && isEditing && (
              <div className="dbs-weekly-buttons-wrapper">
                <Button
                  onClick={handleSave}
                  style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                  className="dbs-gradient-btn"
                >
                  {formatMessage({ id: 'dbs_confirm' })}
                </Button>
                <Button
                  onClick={handleCancel}
                  style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                  className="dbs-outline-btn"
                >
                  {formatMessage({ id: 'dbs_cancel' })}
                </Button>
              </div>
            )}
          </div>
        }
      >
        <TimeLine
          ref={timelineRef}
          variant="monthly"
          cards={timelineCards}
          projectOptions={calendarProjectOptions} // for dropdown project selection (calendar)
          loading={tableLoading || searchLoading}
          isEditing={isEditing}
          canEdit={buttonPermission.mktBudgetApplicationCalendar}
          onCardsChange={handleCardsChange}
          onDeleteCard={handleDeleteCard}
          onProjectSearch={handleProjectSearch} // for server-side project search
        />
      </CardCommon>
      <PlainText
        ref={MKTCostRef}
        url="/api/v1/mgmt_pc/note/get"
        editUrl="/api/v1/mgmt_pc/note/update"
        params={{
          source: "mkt",
          logical_flag: "has_date_latest"
        }}
        showDatePicker={false}
        showTimeInTitle={true}
        selectedMonth={selectedMonthMoment}
        maxDataTime={maxDataTime}
        editable={true}
        userPermissions={{ canEdit: buttonPermission.notes, noAccessHide: getAppendixNotesPermission('publishing_mktcost_notes') }}
        tipString={'Data is updated by the Business Operations Management team.'}
        onEditClick={(type) => {
          // Track buried point for Notes Edit button or Add Content link click
          const extInfo = type === 'Add Content Link' 
            ? 'Notes Edit - Add Content Link'
            : 'Notes Edit - Edit Button';
          const btnPoint = createBtnPoint(
            MKTCostTab,
            'notesEdit',
            extInfo
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }}
        onEditingChange={handleNotesEditingChange}
        // Auto-save feature
        autoSavePageKey={EDIT_PAGE_KEY_MAP.publishing_mkt_cost_notes}
      />

      {/* Auto-save modal for MKT Cost by Projects */}
      <AutoSaveModal
        visible={MKTAutoSaveVisible}
        onClose={handleMKTAutoSaveCancel}
        onConfirm={handleMKTAutoSaveConfirm}
      />
      {/* Auto-save modal for MKT Budget Application Calendar */}
      <AutoSaveModal
        visible={calendarAutoSaveVisible}
        onClose={() => {
          handleCalendarAutoSaveCancel();
          setIsEditing(false);
          getAllYearData();
        }}
        onConfirm={() => {
          handleCalendarAutoSaveConfirm();
          setIsEditing(true);
        }}
      />

      {/* 🚀 RoutePrompt for MKT Cost by Projects */}
      <RoutePrompt
        when={hasMKTUnsavedChanges}
        onOk={async () => {
          const editedData = MKTRef.current?.getEditData?.() || [];
          await handleSaveMKTCost(editedData);
          clearMKTAutoSave();
          setIsMKTEditing(false);
        }}
      />

      {/* 🚀 RoutePrompt for MKT Budget Application Calendar */}
      <RoutePrompt
        when={hasCalendarUnsavedChanges}
        onOk={async () => {
          await handleSave();
        }}
      />
    </div>
  );
};
